import { memo, useMemo } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { calculateDomain, calculateTicks } from "../../../lib/functions";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { useTranslation } from "react-i18next";

interface AreaChartComponentProps {
  data: {
    date: string;
    value: number;
  }[];
}

const CustomDot: React.FC<{
  cx: number;
  cy: number;
  value: number;
  active: boolean;
}> = ({ cx, cy, value, active }) => {
  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill="white" stroke="#403f68" />
      {active && (
        <>
          <text
            x={cx}
            y={cy - 10}
            fill="#403f68"
            textAnchor="middle"
          >{`${value}`}</text>
          <line
            x1={cx}
            y1={cy}
            x2={cx}
            y2={300}
            stroke="#403f68"
            strokeWidth={2}
            strokeDasharray="3 3"
          />
        </>
      )}
    </g>
  );
};

const AreaChartComponent = ({ data }: AreaChartComponentProps) => {
  const { i18n } = useTranslation();

  const values = useMemo(() => data?.map((item) => item.value), [data]);

  const [roundedMin, roundedMax] = calculateDomain(values);

  const ticks = calculateTicks(roundedMin, roundedMax, 1);

  return (
    <ResponsiveContainer width="100%" height={354.5} className="-ml-7">
      <AreaChart data={data}>
        <defs>
          <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#403f68" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#FFFFFF00" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#EEEEEE" />
        <XAxis
          dataKey="date"
          className="text-[10px] font-light"
          tickFormatter={(value) =>
            i18n.language === "ar"
              ? dayjs(value).locale("ar").format("MMM D")
              : dayjs(value).format("MMM D")
          }
        />
        <YAxis
          ticks={ticks}
          domain={[roundedMin, roundedMax]}
          className="text-[10px] font-light"
        />
        <Tooltip />
        <Legend />
        <Area
          type="monotone"
          dataKey="value"
          stroke="#403f68"
          strokeWidth={2.2}
          dot={({
            cx,
            cy,
            value,
            active,
          }: {
            cx: number;
            cy: number;
            value: number;
            active: boolean;
          }) => <CustomDot cx={cx} cy={cy} value={value} active={active} />}
          fillOpacity={1}
          fill="url(#colorPv)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default memo(AreaChartComponent);
